import { useFormikContext } from 'formik';
import type { FC } from 'react';
import React, { useState } from 'react';
import { DEF_PG_SIZE_SUB_TABS } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import MapRelayColumns from '../../models/ColumnDefinitions/MapRelayColumns';
import { Floor } from '../../models/Floor';
import { ListQuery } from '../../models/ListQuery';
import { MapRelay, MapRelayWrapper } from '../../models/MapRelay';
import MapRelaySearch from '../../models/search/MapRelaySearch';
import TableTab from '../tabs/table-tab';
import { WizardStepLayout } from '../wizard/wizard-summary-layout';

interface MapRelayStepProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

interface IoModuleForMapping {
    id: number;
    name: string;
    fixedAddress: string;
    totalRelaysSupported: number;
}

const MapRelayStep: FC<MapRelayStepProps> = ({ onNext, onBack, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { values, setFieldValue } = useFormikContext<any>();
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [mapRelays, setMapRelays] = useState<MapRelay[]>([]);
    const [warningMessage, setWarningMessage] = useState<string>("");
    const [ioModulesForMapping, setIoModulesForMapping] = useState<IoModuleForMapping[]>(values.ioModules ? [...values.ioModules] : []);

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setStepDescriptions(prev => ({ ...prev, step6: translate.byKey(TranslationKeys.done) }));
        setFieldValue("mapRelays", mapRelays);
        onNext(e);
    }

    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }

    const doCalculateMapRelays = (ioModules: IoModuleForMapping[]): MapRelay[] => {
        const mapRelays: MapRelay[] = [];
        const floorLevels: number[] = values.floors ? [...values.floors.map((floor: Floor) => parseInt(floor.level))] : [];
        const relayStart: number = 1;

        while (ioModules.length > 0 && floorLevels.length > 0) {
            const ioModule = ioModules.shift();

            if (!ioModule) {
                break;
            }

            const floorLevelsForThisModule = floorLevels.splice(0, ioModule.totalRelaysSupported);
            const relaysAndInputs: string = `${relayStart}-${relayStart + floorLevelsForThisModule.length - 1}`;

            mapRelays.push({
                id: ioModule.id,
                name: ioModule.name,
                fixedAddress: ioModule.fixedAddress,
                floors: translate.byKeyFormatted(TranslationKeys.fromTo, { from: floorLevelsForThisModule[0], to: floorLevelsForThisModule[floorLevelsForThisModule.length - 1] }),
                relays: relaysAndInputs,
                inputs: relaysAndInputs,
            });

        }

        if (floorLevels.length > 0) {
            setWarningMessage(translate.byKeyFormatted(TranslationKeys.mapRelaysFloorWarning, { unmappedFloors: floorLevels.length }));
            setFieldValue("unmappedFloors", floorLevels);
        }

        if (ioModules.length > 0) {
            setWarningMessage(translate.byKeyFormatted(TranslationKeys.mapRelaysIOModuleWarning, { extraIOModules: ioModules.length }));
            setFieldValue("unmappedIOModules", ioModules);
        }

        const mappedIoFixedAddresses = mapRelays.map(m => m.fixedAddress);
        setIoModulesForMapping(prev => prev.filter(ioModuleForMapping => mappedIoFixedAddresses.includes(ioModuleForMapping.fixedAddress)));
        setMapRelays(mapRelays); // So that we can set this to formik when the user clicks next
        return mapRelays;
    }

    const getMapRelays = (listParameter?: ListQuery): Promise<MapRelayWrapper> => {
        const rows: MapRelay[] = doCalculateMapRelays([...ioModulesForMapping]);
        return Promise.resolve({
            rows,
            pagination: {
                totalCount: 0,
                maxPageSize: 0,
                pageSize: 0,
                totalPages: 0,
                pageNumber: 0
            }
        });
    }

    const onMoveUpOrDown = (item: MapRelay, _: number, direction: 'up' | 'down') => {
        updateTable((tableData: MapRelay[]) => {
            const indexOfToBeSwapped = ioModulesForMapping.findIndex(ioModule => ioModule.fixedAddress === item.fixedAddress);
            const indexOfToBeSwappedWith = direction === 'up' ? indexOfToBeSwapped - 1 : indexOfToBeSwapped + 1;
            const toBeSwappedwith = ioModulesForMapping[indexOfToBeSwappedWith];
            const toBeSwapped = ioModulesForMapping[indexOfToBeSwapped];

            if (toBeSwapped === undefined || toBeSwappedwith === undefined) {
                return tableData;
            }

            ioModulesForMapping[indexOfToBeSwapped] = toBeSwappedwith;
            ioModulesForMapping[indexOfToBeSwappedWith] = toBeSwapped;
            
            const newMapRelays = doCalculateMapRelays([...ioModulesForMapping]);
            return [...newMapRelays];
        });
    }

    return <WizardStepLayout onBack={onBack} onNext={onLocalNext} nextDisabled={mapRelays.length === 0}>
        <div className='flex w-full h-full' style={{ maxHeight: '365px' }}>
            <TableContextProvider
                dataMethod={(listParameter?: ListQuery) => getMapRelays(listParameter)}
                tableDescriptor={MapRelayColumns}
                searchDescriptor={MapRelaySearch}
                useTable
                usePagination
                useFilter
                useSearch
                useSort
                maxPageSize={DEF_PG_SIZE_SUB_TABS}
                canExportReport={false}
                tableDataUpdate={tableDataUpdate}
                onMoveUpOrDown={onMoveUpOrDown}>
                <TableTab
                    title={translate.byKey(TranslationKeys.mapRelays)}
                    isPaginationSupported={false}
                    selectRows={false}
                    cyName="mapRelayTable" />
            </TableContextProvider>
        </div>
        <div className={`flex flex-col ${warningMessage ? '' : 'hidden'}`} id='relayMappingWarningId'>
            <span className='text-orange-600 font-semibold'>{translate.byKey(TranslationKeys.warning)}</span>
            <span className={`text-xs`} >
                {warningMessage}
            </span>
        </div>
    </WizardStepLayout>
}

export default MapRelayStep;